/**
 * Works backwards from what you're shooting to exact dial positions.
 *
 * You tell it three things — subject, how much background blur you want, and
 * how bright it is. It solves the exposure and hands back what to set.
 *
 * Everything it produces is checked against resolve(): a recommendation that
 * doesn't actually expose correctly is a bug, and the tests enforce that.
 *
 * Copy rules, learned from putting this in front of non-technical owners:
 *  - Name where a control physically is, not just what it's called.
 *  - Never quote a number that isn't engraved on the dial.
 *  - Never suggest a fix using a control that's dead in the recommended mode.
 */

import {
  F_NUMBERS,
  SHUTTER_BY_ID,
  SHUTTER_DETENTS,
  formatShutter,
  snapToFNumber,
  snapToShutter,
  ISO_DETENTS,
} from './dials'
import {
  KIT_16_50,
  clampFocal,
  handheldThresholdSeconds,
  isPrime,
  maxApertureAt,
  minApertureAt,
  type Lens,
} from './lens'
import type {
  CameraState,
  ExposureMode,
  IsoDialId,
  ShutterDialId,
  Warning,
} from './types'

/** How much of the background you want thrown out of focus. */
export type BlurPreference = 'max' | 'some' | 'sharp'

export const BLUR_OPTIONS: Array<{
  id: BlurPreference
  label: string
  hint: string
}> = [
  {
    id: 'max',
    label: 'Blurry background',
    // Don't over-promise: this lens can only do so much, and finding that out
    // in a footnote after choosing feels like a bait and switch.
    hint: 'Subject pops — as much background softness as this lens can give',
  },
  {
    id: 'some',
    label: 'A little blur',
    hint: 'Subject stands out, but you can still see what is behind it',
  },
  { id: 'sharp', label: 'Everything sharp', hint: 'Front to back in focus' },
]

export interface SubjectProfile {
  id: string
  label: string
  /** Fastest shutter needed to freeze this subject, in seconds. */
  minShutterSeconds: number
  /**
   * Nothing in the frame is moving. Steady the camera and you can expose for
   * as long as you like, which is how you shoot these at night without grain.
   */
  stillSubject: boolean
  /** Which mode gives you the most useful control over this subject. */
  mode: ExposureMode
  /** Where to zoom to when blur preference doesn't override it. */
  suggestedFocal: number
  /** What most people want here, used as the default blur choice. */
  defaultBlur: BlurPreference
  /** One line of technique that matters more than the settings. */
  tip: string
}

/**
 * Ordered by how often this owner actually reaches for them — the first tile
 * is also the default answer, so the most-shot subject leads. Portraits and
 * parties sit last by preference, not because they matter less to the model.
 *
 * Every entry is a thing in front of the camera, never a lighting condition.
 * "Night" belongs to the light question, not here — a building at night is
 * still a building, and asking twice just double-counts the same answer.
 */
export const SUBJECTS: SubjectProfile[] = [
  {
    id: 'buildings',
    label: 'Buildings',
    minShutterSeconds: 1 / 60,
    stillSubject: true,
    mode: 'A',
    suggestedFocal: 16,
    defaultBlur: 'sharp',
    tip: 'Stand square to the building and keep the camera level, or the walls lean in.',
  },
  {
    id: 'landscape',
    label: 'Landscape',
    minShutterSeconds: 1 / 60,
    stillSubject: true,
    mode: 'A',
    suggestedFocal: 16,
    defaultBlur: 'sharp',
    tip: 'Focus about a third into the scene, not on the horizon. Keep ISO low — you have time.',
  },
  {
    id: 'street',
    label: 'Street',
    minShutterSeconds: 1 / 250,
    stillSubject: false,
    mode: 'A',
    suggestedFocal: 24,
    defaultBlur: 'some',
    tip: 'Pre-set everything so you only have to raise the camera and press. Hesitation loses the shot.',
  },
  {
    id: 'food',
    label: 'Food & details',
    minShutterSeconds: 1 / 60,
    stillSubject: true,
    mode: 'A',
    suggestedFocal: 50,
    defaultBlur: 'max',
    tip: 'Shoot with the light coming from behind or the side of the plate, never from behind you.',
  },
  {
    id: 'action',
    label: 'Action',
    minShutterSeconds: 1 / 500,
    stillSubject: false,
    mode: 'S',
    suggestedFocal: 50,
    defaultBlur: 'some',
    tip: 'Set the shutter and let the camera handle the rest. Shoot a burst — one frame in five will be the keeper.',
  },
  {
    id: 'kids',
    label: 'Kids & pets',
    minShutterSeconds: 1 / 250,
    stillSubject: false,
    mode: 'A',
    suggestedFocal: 35,
    defaultBlur: 'some',
    tip: 'They move faster than you expect. If shots come out soft, the shutter is too slow — not the focus.',
  },
  {
    id: 'portrait',
    label: 'Portraits',
    minShutterSeconds: 1 / 125,
    stillSubject: false,
    mode: 'A',
    suggestedFocal: 50,
    defaultBlur: 'max',
    tip: 'Focus on the near eye. Put distance between them and the background — that does more for blur than any setting.',
  },
  {
    id: 'indoors',
    label: 'Parties & gatherings',
    minShutterSeconds: 1 / 125,
    stillSubject: false,
    mode: 'A',
    suggestedFocal: 24,
    defaultBlur: 'some',
    tip: 'Get near a window if there is one. Indoor light is much dimmer than your eyes suggest.',
  },
]

export const SUBJECT_BY_ID = new Map(SUBJECTS.map((s) => [s.id, s]))

export interface RecommendInput {
  subjectId: string
  blur: BlurPreference
  /** Scene brightness as EV at ISO 100. */
  sceneEv100: number
  /** A tripod removes the handshake limit and lets ISO stay low. */
  tripod: boolean
}

/** Stable identity for each row, so code and tests don't depend on display copy. */
export type InstructionId = 'expComp' | 'mode' | 'shutter' | 'iso' | 'aperture' | 'zoom' | 'check'

export interface DialInstruction {
  id: InstructionId
  /** What to touch, including where to find it on the camera. */
  control: string
  /** What to set it to. */
  setTo: string
  /** Why, in plain English. */
  why: string
  /** True when this control does nothing in the recommended mode. */
  inert?: boolean
}

export interface Recommendation {
  subject: SubjectProfile
  headline: string
  mode: ExposureMode
  aperture: number
  shutterSeconds: number
  iso: number
  focalLength: number
  shutterDial: ShutterDialId
  isoDial: IsoDialId
  instructions: DialInstruction[]
  warnings: Warning[]
  tip: string
  /** True when no settings will work — the UI dims the rows and leads with why. */
  impossible: boolean
  /** Ready to load straight into the Top Plate Simulator. */
  cameraState: CameraState
}

/** Above this, grain starts costing more than the shot gains. */
const ISO_COMFORT_CEILING = 6400
const ISO_HARD_CEILING = 51200

/** What the camera will select on its own, at either end. */
const FASTEST_SHUTTER = 1 / 4000
const SLOWEST_AUTO_SHUTTER = 30

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** The exposure time that lands correctly at ISO 100 — the cleanest option. */
function shutterAtBaseIso(aperture: number, sceneEv100: number): number {
  return (aperture * aperture) / Math.pow(2, sceneEv100)
}

/**
 * Where max-blur falls back to when the long end costs too much grain: the
 * kit zoom holds f/5.6 here, a full stop's worth of ISO for ~20% less blur.
 */
const BLUR_FALLBACK_FOCAL = 35

/** Full-stop ISO values available on the dial, native range only. */
const ISO_DIAL_VALUES = ISO_DETENTS.filter((d) => !d.extended)

function isoAtOrAbove(required: number): { value: number; id: IsoDialId } {
  const found =
    ISO_DIAL_VALUES.find((d) => d.value >= required) ?? ISO_DIAL_VALUES[ISO_DIAL_VALUES.length - 1]
  return { value: found.value, id: found.id }
}

/**
 * Nearest dial detent in stops, either side. Used only in light-starved S
 * mode, where a third-stop of underexposure beats a full stop of grain.
 */
function isoNearestDetent(required: number): { value: number; id: IsoDialId } {
  const found = ISO_DIAL_VALUES.reduce((best, d) =>
    Math.abs(Math.log2(d.value / required)) < Math.abs(Math.log2(best.value / required)) ? d : best,
  )
  return { value: found.value, id: found.id }
}

/** Nearest full-stop shutter detent that is at least as fast as `seconds`. */
function shutterDetentAtOrFasterThan(seconds: number): ShutterDialId {
  const speeds = SHUTTER_DETENTS.filter((d) => d.seconds !== null && d.id !== 'X')
  const candidates = speeds.filter((d) => d.seconds! <= seconds)
  if (candidates.length === 0) return '1/4000'
  // The slowest of the fast-enough options loses the least light.
  return candidates.reduce((best, d) => (d.seconds! > best.seconds! ? d : best)).id
}

/** ISO needed to expose correctly at this aperture and shutter. */
function isoFor(aperture: number, seconds: number, sceneEv100: number): number {
  const settingsEv = Math.log2((aperture * aperture) / seconds)
  return 100 * Math.pow(2, settingsEv - sceneEv100)
}

export function recommend(input: RecommendInput, lens: Lens = KIT_16_50): Recommendation {
  const subject = SUBJECT_BY_ID.get(input.subjectId) ?? SUBJECTS[0]
  const mode = subject.mode
  const warnings: Warning[] = []

  // Steady camera plus a subject that isn't moving means there is no reason to
  // hurry: run the exposure long and keep ISO at its floor. This is how you
  // shoot a building at night cleanly, and it's why "night" doesn't need to be
  // a subject of its own.
  const longExposureOk = input.tripod && subject.stillSubject

  /**
   * The whole exposure solve for one zoom position. Kept as a function so the
   * max-blur path can compare the long end against the 35mm fallback and take
   * whichever costs less grain.
   */
  const solveAtFocal = (focalLength: number) => {
    const maxAperture = maxApertureAt(lens, focalLength)
    const minAperture = minApertureAt(lens, focalLength)
    const shakeLimit = handheldThresholdSeconds(focalLength)

    const freezeShutter = input.tripod
      ? subject.minShutterSeconds
      : Math.min(subject.minShutterSeconds, shakeLimit)

    // ── Aperture from the blur preference ──────────────────────────────────
    let aperture: number
    if (input.blur === 'max') {
      aperture = maxAperture
    } else if (input.blur === 'some') {
      // On this slow zoom, wide open already is "a little blur". Only spend
      // the ×1.4 stop-down when the light is free — never at real ISO cost.
      const stopped = Math.min(minAperture, Math.max(maxAperture, snapToFNumber(maxAperture * 1.4)))
      aperture =
        !longExposureOk && isoFor(stopped, freezeShutter, input.sceneEv100) > 1600
          ? maxAperture
          : stopped
    } else {
      // f/8 is the sharpness sweet spot on this sensor; past f/11 diffraction bites.
      aperture = Math.min(minAperture, Math.max(maxAperture, 8))
    }

    // ── Shutter speed the subject demands ──────────────────────────────────
    let requiredShutter = longExposureOk
      ? clamp(shutterAtBaseIso(aperture, input.sceneEv100), FASTEST_SHUTTER, SLOWEST_AUTO_SHUTTER)
      : freezeShutter

    let requiredIso = isoFor(aperture, requiredShutter, input.sceneEv100)

    // Track whether the light forced us wide open, so the copy can say so
    // instead of claiming we stopped down on purpose.
    let openedUpForLight = false
    if (requiredIso > ISO_COMFORT_CEILING && aperture > maxAperture) {
      aperture = maxAperture
      openedUpForLight = true
      if (longExposureOk) {
        requiredShutter = clamp(
          shutterAtBaseIso(aperture, input.sceneEv100),
          FASTEST_SHUTTER,
          SLOWEST_AUTO_SHUTTER,
        )
      }
      requiredIso = isoFor(aperture, requiredShutter, input.sceneEv100)
    }

    // The opposite failure: a fast lens wide open in bright light can need a
    // shutter faster than the camera's 1/4000 ceiling. Stop down just enough
    // to stay inside it — the real-world fix, short of an ND filter.
    let stoppedDownForLight = false
    if (shutterAtBaseIso(aperture, input.sceneEv100) < FASTEST_SHUTTER) {
      const needed = Math.sqrt(FASTEST_SHUTTER * Math.pow(2, input.sceneEv100))
      const narrower = F_NUMBERS.find((f) => f >= needed) ?? minAperture
      aperture = clamp(narrower, maxAperture, minAperture)
      stoppedDownForLight = true
      // A tripod doesn't make the sun dimmer — the long-exposure path needs
      // its shutter recomputed for the narrower aperture too.
      if (longExposureOk) {
        requiredShutter = clamp(
          shutterAtBaseIso(aperture, input.sceneEv100),
          FASTEST_SHUTTER,
          SLOWEST_AUTO_SHUTTER,
        )
      }
      requiredIso = isoFor(aperture, requiredShutter, input.sceneEv100)
    }

    // Nothing in the frame moves and the lens is stabilised, so hand shake is
    // the only speed limit — and VR removes stops of it. Credit a conservative
    // 2.5 of the rated 4.5 before accepting grain.
    let vrBraced = false
    if (requiredIso > ISO_COMFORT_CEILING && !input.tripod && subject.stillSubject && lens.hasVr) {
      const relaxed = shakeLimit * Math.pow(2, Math.min(2.5, lens.vrStops))
      if (relaxed > requiredShutter) {
        requiredShutter = relaxed
        requiredIso = isoFor(aperture, requiredShutter, input.sceneEv100)
        vrBraced = true
      }
    }

    return {
      focalLength,
      maxAperture,
      minAperture,
      shakeLimit,
      aperture,
      requiredShutter,
      requiredIso,
      openedUpForLight,
      stoppedDownForLight,
      vrBraced,
    }
  }

  // ── Where to zoom ────────────────────────────────────────────────────────
  // Max blur means the long end: on a slow zoom, separation comes from focal
  // length and subject distance, not from a wide aperture it doesn't have.
  // A prime collapses all of this to its one focal length.
  const primaryFocal = clampFocal(
    lens,
    input.blur === 'max'
      ? lens.maxFocal
      : input.blur === 'sharp'
        ? Math.min(subject.suggestedFocal, 24)
        : subject.suggestedFocal,
  )

  let solve = solveAtFocal(primaryFocal)

  // When the long end is costing serious grain, check the 35mm fallback:
  // f/5.6 instead of f/6.3 gives up ~20% of the blur disc, and if that lands
  // a full ISO dial stop lower, it's the better photograph.
  let zoomedOutForLight = false
  if (
    input.blur === 'max' &&
    mode === 'A' &&
    !longExposureOk &&
    solve.requiredIso > ISO_COMFORT_CEILING &&
    primaryFocal === lens.maxFocal &&
    lens.maxFocal > BLUR_FALLBACK_FOCAL
  ) {
    const alt = solveAtFocal(BLUR_FALLBACK_FOCAL)
    const primaryDetent = isoAtOrAbove(clamp(solve.requiredIso, 100, ISO_HARD_CEILING)).value
    const altDetent = isoAtOrAbove(clamp(alt.requiredIso, 100, ISO_HARD_CEILING)).value
    if (altDetent * 2 <= primaryDetent) {
      solve = alt
      zoomedOutForLight = true
    }
  }

  const { focalLength, maxAperture, shakeLimit, openedUpForLight, stoppedDownForLight, vrBraced } =
    solve
  const { aperture, requiredShutter, requiredIso } = solve

  const clampedIso = Math.min(requiredIso, ISO_HARD_CEILING)
  let iso = isoAtOrAbove(Math.max(100, clampedIso))

  // ISO snaps up to a full stop, so recompute the shutter it actually buys.
  let finalShutter = snapToShutter(
    Math.min(
      requiredShutter,
      (aperture * aperture) / Math.pow(2, input.sceneEv100 + Math.log2(iso.value / 100)),
    ),
  )

  let shutterDial: ShutterDialId =
    mode === 'S' ? shutterDetentAtOrFasterThan(finalShutter) : '1/3STEP'

  // Light-starved S mode: the ISO snap-up plus the dial round-up were
  // compounding into a detent a full stop faster than the subject needs,
  // spending scarce ISO on shutter speed nobody asked for. Sit on the slowest
  // engraved detent that still freezes the subject and meter the ISO to it.
  if (mode === 'S' && requiredIso > ISO_COMFORT_CEILING) {
    shutterDial = shutterDetentAtOrFasterThan(requiredShutter)
    const detentSeconds = SHUTTER_BY_ID.get(shutterDial)?.seconds ?? finalShutter
    iso = isoNearestDetent(clamp(isoFor(aperture, detentSeconds, input.sceneEv100), 100, ISO_HARD_CEILING))
    finalShutter = detentSeconds
  }

  // In S mode the user sets an engraved detent, so every number we quote must
  // be that detent — not the finer value the solver worked with.
  const quotedShutter =
    mode === 'S' ? (SHUTTER_BY_ID.get(shutterDial)?.seconds ?? finalShutter) : finalShutter

  const impossible = requiredIso > ISO_HARD_CEILING

  // ── Warnings ─────────────────────────────────────────────────────────────
  if (impossible) {
    warnings.push({
      level: 'problem',
      title: 'There is not enough light for this',
      detail: input.tripod
        ? 'Even resting on something solid, this is too dark to freeze a moving subject. Turn on more light, or wait for a moment when they hold still.'
        : 'These settings will still come out dark. Your options: turn on more light, move them nearer a window, rest the camera on something and wait for a still moment — or pick "A little blur" and accept some movement.',
    })
  } else if (iso.value > ISO_COMFORT_CEILING) {
    warnings.push({
      level: 'caution',
      title: `ISO ${iso.value} will look grainy`,
      detail: subject.stillSubject
        ? 'Worth it, but expect visible grain. Nothing in this shot moves, so a steadier camera is the free fix: rest it on a wall, table, or railing — or switch to the tripod option — and this ISO drops right down.'
        : 'Worth it for a shot you would otherwise miss, but expect visible grain. Holding steadier will not help here — the speed is set by your subject moving, not by your hands. More light on your subject is the only thing that drops this.',
    })
  }

  if (requiredIso < 100) {
    warnings.push({
      level: 'info',
      title: 'Plenty of light here',
      detail:
        mode === 'A'
          ? 'ISO 100 is as low as the dial goes. If shots come out too bright, turn the ± dial towards −1 — in this mode that is your brightness control.'
          : 'ISO 100 is as low as the dial goes. If shots come out too bright, use a faster shutter speed.',
    })
  }

  // Only reachable when the solver deliberately went slower than the bare
  // handhold floor, trusting VR on a still subject.
  if (!input.tripod && finalShutter > shakeLimit) {
    const safeDetent = SHUTTER_BY_ID.get(shutterDetentAtOrFasterThan(shakeLimit))?.label ?? '1/60'
    warnings.push({
      level: 'caution',
      title: 'Brace — shake reduction is carrying this shot',
      detail: `${formatShutter(finalShutter)} is slower than the usual ${safeDetent} handheld floor at ${focalLength}mm. Nothing in the frame moves and your lens's shake reduction is built for exactly this — but help it: tuck your elbows in, breathe out as you press, or lean on something solid.`,
    })
  }

  if (zoomedOutForLight) {
    warnings.push({
      level: 'info',
      title: 'Zoomed to 35 instead of 50',
      detail:
        'In this light, the long end of the zoom would cost double the grain for barely more blur. At 35 you keep almost all the look at half the ISO — step closer to make up the framing.',
    })
  }

  if (input.blur === 'max' && maxAperture > 5) {
    warnings.push({
      level: 'info',
      title: 'Where the blur actually comes from',
      detail: `Your ${lens.shortName} lens can only blur a little on its own. What does the real work: get as close to your subject as you can, and get the background as far behind them as you can. That beats any setting on this card.`,
    })
  }

  // The opposite problem: a fast prime wide open has almost no depth at all.
  if (input.blur === 'max' && aperture <= 2) {
    warnings.push({
      level: 'info',
      title: `Focus is razor-thin at f/${aperture}`,
      detail:
        'Up close, the zone of sharp focus is millimetres deep. Put the focus point on the near eye, not the face in general. Two people in the shot? Stop down to f/2.8 or line them up at the same distance — one of them will be soft otherwise.',
    })
  }

  // ── Build the instructions ───────────────────────────────────────────────
  const instructions: DialInstruction[] = []

  // First, because it persists between outings and silently ruins everything.
  instructions.push({
    id: 'expComp',
    control: '± dial — top right of the camera',
    setTo: '0',
    why: 'Check this one even if you never touch it. It stays where it was last left, and if it is not on 0 every photo comes out too dark or too bright — nothing else on this list will fix that.',
  })

  instructions.push({
    id: 'mode',
    control: 'Mode selector — the small ring under the ISO dial',
    setTo: mode === 'A' ? 'A — not AUTO' : mode,
    why:
      mode === 'S'
        ? 'You lock the shutter speed so the action stays sharp; the camera works out the rest.'
        : 'A is not the same as AUTO — it is the next position round. In AUTO the camera ignores your ISO dial and everything below stops working. In A you choose how blurry the background is and the camera works out the shutter speed.',
  })

  if (mode === 'S') {
    instructions.push({
      id: 'shutter',
      control: 'Shutter dial — top right, beside the ± dial',
      setTo: SHUTTER_DETENTS.find((d) => d.id === shutterDial)?.engraved ?? shutterDial,
      why: `Fast enough to freeze ${subject.label.toLowerCase()}.`,
    })
  }

  instructions.push({
    id: 'iso',
    control: 'ISO dial — the big numbered dial, top left',
    setTo: String(iso.value),
    why:
      iso.value <= 400
        ? 'Low ISO for the cleanest possible image — you have the light for it.'
        : iso.value <= 1600
          ? 'Bright enough for this light, and still clean.'
          : iso.value <= 6400
            ? 'Some speckle at this level, but nothing that will spoil the shot.'
            : 'This is very high — expect real grain. It is this or a blurred photo, and grain still looks like your subject.',
  })

  instructions.push({
    id: 'aperture',
    control: 'Front dial — the wheel by the shutter button',
    setTo: `f/${aperture}`,
    why:
      input.blur === 'max'
        ? stoppedDownForLight
          ? `f/${aperture}, not wide open — this much light would overshoot the camera's 1/4000 shutter ceiling any wider. Still plenty of blur; for wide-open looks in bright sun you'd need an ND filter.`
          : `f/${aperture} is the smallest f-number your lens reaches at ${focalLength}mm, so this is as much background blur as it can give. Smaller f-number = more blur.`
        : input.blur === 'sharp'
          ? openedUpForLight
            ? `f/${aperture} is as wide as your lens goes at ${focalLength}mm — there isn't enough light to stop down to f/8 for true front-to-back depth. Keep everything that matters at a similar distance; the far background may go a little soft.`
            : 'The sharpest this lens gets, and deep enough to hold the whole scene in focus.'
          : openedUpForLight || aperture <= maxAperture + 0.001
            ? `f/${aperture} is as wide as your lens goes at ${focalLength}mm. I wanted to close down a little for extra sharpness, but there isn't enough light — so you're getting all of it.`
            : 'Slightly narrower than wide open — your subject still stands out from the background, but stays sharp.',
  })

  if (isPrime(lens)) {
    instructions.push({
      id: 'zoom',
      control: 'Framing — no zoom on this lens',
      setTo: `${focalLength}mm`,
      why: 'This is a prime lens: one focal length, so you zoom with your feet. Walk closer to tighten the frame, step back to widen it — moving also changes the background more than any dial does.',
    })
  } else {
    instructions.push({
      id: 'zoom',
      control: 'Zoom — the wide ring on the lens',
      setTo: `${focalLength}mm`,
      why:
        input.blur === 'max'
          ? zoomedOutForLight
            ? `The barrel reads ${focalLength} — backed off from 50 because in this light the long end costs double the grain for barely more blur. Get close to your subject instead.`
            : 'Zoomed all the way in (the barrel reads 50) — the more you zoom, the softer the background. You will need to step back a few paces to fit them in; if you cannot, zoom to 35 and pick "A little blur" instead.'
          : input.blur === 'sharp'
            ? 'Wide, to take in the scene and keep it sharp from front to back.'
            : 'A natural-looking framing for this subject.',
    })
  }

  // In A mode this does nothing, so it goes last — a do-nothing should never be
  // the second thing anyone reads.
  if (mode !== 'S') {
    instructions.push({
      id: 'shutter',
      control: 'Shutter dial — top right, beside the ± dial',
      setTo: 'Leave it',
      why: 'In this mode the shutter dial does nothing at all — the camera picks the speed. Skip this one.',
      inert: true,
    })
  }

  if (mode === 'A') {
    const freeze = Math.round(1 / subject.minShutterSeconds)
    instructions.push(
      longExposureOk
        ? {
            id: 'check',
            control: 'Before you shoot',
            setTo: 'Hands off',
            why: 'The camera may pick a long exposure, and that is exactly right when it is resting on something solid. Do not touch it while the shot is being taken — use the self-timer so pressing the button does not shake it.',
          }
        : vrBraced
          ? {
              id: 'check',
              control: 'Before you shoot',
              setTo: 'Check',
              why: `Half-press the shutter button and look at the speed. Around ${formatShutter(finalShutter)} is expected here — that is deliberately slow, with shake reduction doing the steadying. If it reads much slower than that, the light is dimmer than you told me; click the ISO dial up one stop.`,
            }
          : {
              id: 'check',
              control: 'Before you shoot',
              setTo: 'Check',
              why: `Half-press the shutter button and look at the speed the camera shows. It should read 1/${freeze} or faster. If it is slower than that, the light is dimmer than you told me — click the ISO dial up one stop and look again.`,
            },
    )
  }

  const cameraState: CameraState = {
    mode,
    shutterDial,
    isoDial: iso.id,
    expCompDial: 0,
    photoVideo: 'photo',
    mainCommandDialShutterSeconds: finalShutter,
    subCommandDialAperture: aperture,
    commandDialExpComp: 0,
    focalLength,
    sceneEv100: input.sceneEv100,
  }

  return {
    subject,
    headline: buildHeadline(subject, input.blur, quotedShutter, aperture, iso.value),
    mode,
    aperture,
    shutterSeconds: finalShutter,
    iso: iso.value,
    focalLength,
    shutterDial,
    isoDial: iso.id,
    instructions,
    warnings,
    tip: subject.tip,
    impossible,
    cameraState,
  }
}

function buildHeadline(
  subject: SubjectProfile,
  blur: BlurPreference,
  shutter: number,
  aperture: number,
  iso: number,
): string {
  const speed = shutter >= 1 ? `${Number(shutter.toFixed(1))} sec` : formatShutter(shutter)
  const look =
    blur === 'max'
      ? 'a soft background'
      : blur === 'sharp'
        ? 'everything sharp'
        : 'a little separation'
  return `${subject.label} with ${look}: about ${speed} at f/${aperture}, ISO ${iso}.`
}
